import React from 'react';
import { Progress } from 'antd';
import moment from 'moment';
import { isEmpty } from 'lodash';
import {
  Calendar,
  ShoppingCart,
  DollarSign,
  Users,
  ArrowRight,
} from 'lucide-react';

import {
  formatCurrency2SigFig,
  formatWholeNumberNaN,
} from '../../../../../../../core/utils/campaigns';
import KpiBar, { KpiCard } from '../../../../../../shared/designSystem/components/KpiBar';

// =============================================================================
// DetailBar4Col — V1 Transactions 4-KPI strip. Uses the shared `KpiBar` +
// `KpiCard` primitives; flight-dates column passes its body as the card's
// `children` slot (AntD `Progress` bar + date pair) which overrides the
// standard `value` slot. Numeric computation preserved verbatim.
// =============================================================================

const DetailBar4Col = ({
  customers,
  transactions,
  displayData,
  startDate,
  endDate,
  cogs,
  selectedCampaign,
  transactionData,
  byTransactionsRangeDate,
  dataTransactionsRangeDate,
  type,
  determineUniques,
  dateTrue,
  priceVisible,
  budget = 0,
}) => {
  // ─── Computation preserved verbatim from V1 ───────────────────────────────
  const getByDate = transactionData.map((item) => {
    const keyValue = Object.entries(item);
    const transactionRevenue =
      keyValue[keyValue.findIndex((pair) => pair[0] === 'transactionTotal')];
    const x = keyValue[keyValue.findIndex((pair) => pair[0] === 'transaction')];
    return {
      totalImpressions: item.totalImpressions,
      impressionsFromDisplay: item.impressionsFromDisplay,
      transactionRevenue: transactionRevenue[1],
      xAxis: moment(x[1]).format('MM/DD/YYYY'),
    };
  });

  const byDates =
    !isEmpty(getByDate) &&
    getByDate.sort((a, b) => moment(a.xAxis, 'MM/DD/YYYY').diff(moment(b.xAxis, 'MM/DD/YYYY')));

  const counterTransactionsDates = {};
  if (!isEmpty(byDates)) {
    byDates.forEach((obj) => {
      const key = JSON.stringify(obj.xAxis);
      counterTransactionsDates[key] = (counterTransactionsDates[key] || 0) + 1;
    });
  }

  const reduceByDate = getByDate.reduce((map, item) => {
    let summarizedItem = map.get(item.xAxis);
    if (!summarizedItem) {
      summarizedItem = item;
    } else {
      summarizedItem.transactionRevenue += item.transactionRevenue;
    }
    map.set(item.xAxis, summarizedItem);
    return map;
  }, new Map());

  const getArrayByDate = Array.from(reduceByDate.values());
  const setCounterTransactionsDates = Object.values(counterTransactionsDates);
  const initialTransactionData = getArrayByDate.map((obj, i) => ({
    transactions: setCounterTransactionsDates[i],
    ...obj,
  }));

  const getSelectedStartDate =
    byTransactionsRangeDate && byTransactionsRangeDate !== null
      ? moment(byTransactionsRangeDate[0], moment.DATETIME_LOCAL_MS).format('MM/DD/YYYY')
      : null;
  const getSelectedEndDate =
    dataTransactionsRangeDate && byTransactionsRangeDate !== null
      ? moment(byTransactionsRangeDate[1], moment.DATETIME_LOCAL_MS).format('MM/DD/YYYY')
      : null;

  const getRevenue = (item) => item.transactionRevenue;
  const sumTotal = (prev, next) => prev + next;

  const getTotalTransactions = isEmpty(dataTransactionsRangeDate) ? null : transactions;
  const getTotalRevenue = !isEmpty(initialTransactionData)
    ? initialTransactionData.map(getRevenue).reduce(sumTotal)
    : 0;

  const newTransactionData =
    transactionData &&
    transactionData.filter(
      (obj) =>
        moment(obj.transaction).format('MM/DD/YYYY') >= getSelectedStartDate &&
        moment(obj.transaction).format('MM/DD/YYYY') <= getSelectedEndDate
    );
  const getCustomer = dateTrue
    ? determineUniques(newTransactionData)
    : determineUniques(transactionData);

  const getProgress = () => {
    const total = () => {
      const a = isEmpty(byTransactionsRangeDate)
        ? moment(endDate, moment.DATETIME_LOCAL_MS)
        : moment(byTransactionsRangeDate[1]);
      const b = isEmpty(byTransactionsRangeDate)
        ? moment(startDate, moment.DATETIME_LOCAL_MS)
        : moment(byTransactionsRangeDate[0]);
      return a.diff(b, 'days');
    };
    const progress = () => {
      const a = moment();
      const b = isEmpty(byTransactionsRangeDate)
        ? moment(startDate, moment.DATETIME_LOCAL_MS)
        : moment(byTransactionsRangeDate[1]);
      return a.diff(b, 'days');
    };
    const totaldays = total();
    const progressDays = progress();
    if (progressDays >= totaldays) return 100;
    const daysin = totaldays - (totaldays - progressDays);
    return Math.floor((daysin / totaldays) * 100);
  };

  const currentposition = getProgress();

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <KpiBar>
      <KpiCard
        icon={<Calendar size={16} />}
        iconTint="neutral"
        label="Flight Dates"
        labelTooltip="Flight Dates refers to the Start & End Dates of the individual campaign"
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            fontSize: 15,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontWeight: 500,
              color: '#374151',
            }}
          >
            <span>
              {startDate
                ? moment(startDate, moment.DATETIME_LOCAL_MS).format('MM/DD/YYYY')
                : '—'}
            </span>
            <ArrowRight size={14} strokeWidth={2} style={{ color: '#9ca3af' }} />
            <span>
              {endDate ? moment(endDate, moment.DATETIME_LOCAL_MS).format('MM/DD/YYYY') : '—'}
            </span>
          </div>
          <Progress
            showInfo={false}
            percent={currentposition}
            status={currentposition !== 100 ? 'active' : 'normal'}
            strokeColor={'var(--sys, #1890ff)'}
            trailColor="#e5e7eb"
          />
        </div>
      </KpiCard>

      <KpiCard icon={<ShoppingCart size={16} />} iconTint="primary" label="Transactions">
        {transactions
          ? !dateTrue
            ? formatWholeNumberNaN(transactionData.length)
            : formatWholeNumberNaN(getTotalTransactions)
          : '0'}
      </KpiCard>

      <KpiCard
        icon={<DollarSign size={16} />}
        iconTint="emerald"
        label="Influenced Sales"
        labelTooltip="Multi-Touch Attribution (MTA). Using device-level data, this MTA model shows impressions have been served to device identifiers that have also made transactions. This model does not take into account other factors that may have influenced the purchase such as search, newsletters or other marketing techniques. MTA should also not be confused with other models. ie: Last-Interaction or Position-based"
      >
        {cogs
          ? !dateTrue
            ? formatCurrency2SigFig(cogs)
            : getTotalTransactions
            ? formatCurrency2SigFig(getTotalRevenue)
            : '0'
          : '0'}
      </KpiCard>

      <KpiCard icon={<Users size={16} />} iconTint="indigo" label="Customers">
        {formatWholeNumberNaN(getCustomer)}
      </KpiCard>
    </KpiBar>
  );
};

export default DetailBar4Col;
